import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { format } from 'date-fns';
import { AnimatePresence } from 'framer-motion';
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@poseidon/components/cn';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import { Button, Tooltip } from '@poseidon/components/Input2';
import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import type { TipTapDocument, TipTapVariable } from '@poseidon/components/Input2/TipTapInput';
import type { ReminderAction } from '@poseidon/components/ManualSelect';
import { General } from '@poseidon/components/StrokeIcons';
import type { SequenceArmPathSeg } from '@poseidon/types/types/sequenceOverrideKey';
import { NOT_ACCEPTED_SEG, sequenceOverrideKey } from '@poseidon/types/types/sequenceOverrideKey';

import type { SequenceFace } from '~/components/Sequence/SequenceNodeFaces';
import type {
  SequenceContainerStepType,
  SequenceContentOverrides,
  SequenceStep,
  SequenceStepType,
} from '~/components/Sequence/sequenceSteps';
import type { StepSaveStatus } from '~/components/Sequence/SequenceStepSaveTab';
import { useBeforeUnloadGuard } from '@/hooks/useBeforeUnloadGuard';
import { useSequenceFaceActions } from '@/hooks/useSequenceFaceActions';
import { useSequenceFaces } from '@/hooks/useSequenceFaces';
import { api } from '@/utils/api';
import { SequenceGraphInsert } from '~/components/Sequence/SequenceGraphInsert';
import { SequenceHistoryColumn } from '~/components/Sequence/SequenceHistoryColumn';
import { SequenceGraphNode } from '~/components/Sequence/SequenceGraphNode';
import { computeBranchLanes, PREVIEW_GAP, PREVIEW_W, spineNodeHalfWidth } from '~/components/Sequence/sequenceLayout';
import type { WaitUnit } from '~/components/Sequence/WaitDurationInput';
import { SequenceCurrentStepMarker } from '~/components/Sequence/SequenceCurrentStepMarker';
import { SequenceFinishedNode } from '~/components/Sequence/SequenceFinishedNode';
import { SequenceNodeFaces } from '~/components/Sequence/SequenceNodeFaces';
import { otherStatesFrom } from '~/components/Sequence/sequenceOtherStates';
import { SequenceOtherStatesTooltip } from '~/components/Sequence/SequenceOtherStatesTooltip';
import { OWNER_AVATAR_PX } from '~/components/Sequence/SequenceOwnerBadge';
import { SequenceOwnerNode } from '~/components/Sequence/SequenceOwnerNode';
import { SequenceNodePreview } from '~/components/Sequence/SequenceNodePreview';
import { SequencePanZoom } from '~/components/Sequence/SequencePanZoom';
import { SequenceParallelArms } from '~/components/Sequence/SequenceParallelArms';
import { SequenceSwitchBranches, switchFanTotalWidth } from '~/components/Sequence/SequenceSwitchBranches';
import { SequenceSwitchNode } from '~/components/Sequence/SequenceSwitchNode';
import { SequencePrepNode } from '~/components/Sequence/SequencePrepNode';
import type { SequenceContactStepStatus, SequenceStepVisual } from '~/components/Sequence/sequenceStepStatus';
import {
  contactStepStatus,
  isStepContentEditableNow,
  sequenceStepVisual,
} from '~/components/Sequence/sequenceStepStatus';
import { SequencePreStepDetail } from '~/components/Sequence/SequencePreStepDetail';
import { SequencePreStepNode } from '~/components/Sequence/SequencePreStepNode';
import { SequenceStepEditor } from '~/components/Sequence/SequenceStepEditor';
import { SequenceStepPreview, stepHasContentPreview } from '~/components/Sequence/SequenceStepPreview';
import { SequenceTextStrip } from '~/components/Sequence/SequenceTextStrip';
import {
  faceTone,
  insertDisabledReason,
  insertSpineStep,
  isJourneyDoneNode,
  isPrepNode,
  newContainerStep,
  newSequenceStep,
  newSwitchStep,
  nodeStatusLabel,
  PRE_STEP_NODE_IDS,
  previousEmailSubject,
  SEQUENCE_FINISHED_NODE_IDS,
  SEQUENCE_PREP_STEP,
  SEQUENCE_PRE_STEPS,
  sequenceStepIssue,
  manualTaskWaitNodeForStepType,
  STEP_META,
  stepFaceNodeIds,
  stepIsEditable,
  stepOpensEditorOnInsert,
  stepTakesContactOverride,
  SWITCH_FAN_COL_W,
  withContentOverrides,
} from '~/components/Sequence/sequenceSteps';
import { SequenceStepSaveTab } from '~/components/Sequence/SequenceStepSaveTab';
import type { DropTarget, LineAddr, StepAddr } from '~/components/Sequence/sequenceMove';
import { beginStepGesture, readLineRects, resolveDrop } from '~/components/Sequence/sequenceDrop';
import { seqLog, useGraphRender } from '~/components/Sequence/sequenceDebug';
import {
  getLine,
  lineStepCoords,
  moveStep,
  serializeLine,
  SPINE,
  stepAt,
  updateLine,
} from '~/components/Sequence/sequenceMove';

/** The contact's history the locked canvas draws above the sequence, and paging it further back. */
export interface SequenceGraphHistory {
  items: HistoryItem[];
  actions: InteractionHistoryActions;
  /** The server holds history older than `items`; `onLoadOlder` pages it in (the column's "Load older"). */
  hasOlder?: boolean;
  loadingOlder?: boolean;
  /** The last "Load older" failed — the button retries it. */
  olderFailed?: boolean;
  onLoadOlder?: () => void;
}

/** Who the sequence RUNS AS — drawn at the top of the spine by {@link SequenceOwnerNode}. */
export interface SequenceOwner {
  userId: string;
  name: string | null;
  profilePicture: string | null;
}

interface Props {
  teamId: string;
  sequenceId: string;
  initialSteps: SequenceStep[];
  /**
   * The team member every contact in this sequence is worked on behalf of: whose LinkedIn account sends the
   * touches, whose working hours + daily caps gate them, whose to-do list the call/reminder steps land on.
   * Drawn as the circular node above the automatic research steps — a BUILDER row: it describes the whole
   * sequence, not a stage of anyone's journey, so the locked per-contact view leaves it to the sequence's
   * pill on the contact (its avatar) and omits this prop entirely.
   */
  owner?: SequenceOwner;
  /** Reassign the owner. Omit → the node renders as a static badge. */
  onChangeOwner?: (userId: string) => void;
  /** Rich-editor mention data (`@`-variables + `/`-documents) threaded to each step's content editor. */
  variables?: TipTapVariable[];
  documents?: TipTapDocument[];
  /** The team's reusable reminder actions + create hook — threaded to the `reminder` step's dropdown. */
  reminderActions?: ReminderAction[];
  reminderActionsLoading?: boolean;
  onCreateReminder?: (name: string) => Promise<void>;
  reminderCreating?: boolean;
  /**
   * LOCKED per-contact mode (the contact detail page). Freezes the structure — no drag, no `+` insert, no
   * delay / reorder / add / remove — so the graph shows THIS contact's sequence, and clicking a send step
   * edits only its body/subject as a per-contact override (saved via {@link onSaveStepContent}, never to the
   * template). Off (the default) is the full authoring editor, unchanged.
   */
  readOnly?: boolean;
  /** Per-contact content overrides to overlay, keyed by {@link sequenceOverrideKey} (readOnly mode). */
  overrides?: SequenceContentOverrides;
  /**
   * What became of each step for this contact, keyed by {@link sequenceOverrideKey} — done / skipped / where
   * they're resting / a lane they'll never walk. Paints every node (see {@link sequenceStepVisual}) and locks
   * the ones that already went out.
   */
  stepStatus?: Record<string, SequenceContactStepStatus>;
  /** Persist one step's per-contact body/subject override (readOnly mode). Called on editor close when changed. */
  onSaveStepContent?: (overrideKey: string, next: { body?: string; subject?: string }) => Promise<void>;
  /**
   * readOnly mode: mark the call/reminder to-do this contact is currently parked on done (advances the arm
   * past the manual step). Called with the step's manual-task wait node id ({@link manualTaskWaitNodeForStepType})
   * AND its step address, so a sequence with more than one call/reminder step completes the to-do the rep
   * clicked rather than every arm of that contact parked on the same kind of lane.
   * A "Mark done" control appears only on a `current` call/reminder spine step. Wire to `sequence.completeTask`.
   */
  onCompleteManualStep?: (nodeId: string, stepKey: string) => void;
  /**
   * readOnly mode: the journey node this contact rests on right now (`flow.enrollment.current_node_id`).
   * `stepStatus` covers only the AUTHORED steps and every arm exists from enrollment, so it reports step 0
   * as `current` while the contact is in fact still in the automatic front-half — this is what tells the
   * graph they're in Prep (and which stage), and keeps the "now" marker off a step that hasn't run.
   */
  currentNodeId?: string;
  /**
   * readOnly mode: the contact's history, drawn ABOVE the sequence as the past flowing into it — a step per
   * entry on the spine, each opening the full entry (see {@link SequenceHistoryColumn}).
   */
  history?: SequenceGraphHistory;
  /**
   * The canvas IS its section (the interact tab's Graph view): it takes the wheel / trackpad (nothing outside
   * it scrolls), keeps the content reachable while panning, and opens at 100% on where the contact is now,
   * with a "Now" control to get back there — instead of an embedded canvas that leaves the wheel to the page.
   */
  fillsSection?: boolean;
}

// Canvas layout (content-space px). Nodes are centered on a vertical spine; insert slots sit at the
// gap midpoints between them. The automatic pre-steps (research/dossier/qualify/enroll) sit above
// the editable steps.
// The locked per-contact graph's faces map. A module-level constant (not a fresh `{}` per render): the
// disabled faces query still OBSERVES the shared key, so the builder's 5s poll behind the overlay pushes a
// new `data` identity into this component — returning a stable reference keeps that tick from invalidating
// every downstream memo (offGraph) and re-rendering the whole canvas.
const NO_FACES: Record<string, { count: number; sample: SequenceFace[] }> = {};
/** The same stable-empty trick for the per-STEP map (keyed by step address, then by node). */
const NO_STEP_FACES: Record<string, Record<string, { count: number; sample: SequenceFace[] }>> = {};

const CX = 200;
/**
 * The full-section Graph view scrolls edge to edge (it stops at the content box), but the locked view's NOW
 * marker / sent dates hang LEFT of their node — past x = 0 for a wide pill with a long stage label. This much
 * room on the left keeps them inside the content box, so the opening frame and a scroll can always reach them.
 */
const FILL_LEFT_GUTTER = 160;
const NODE_H = 40;
const GAP = 44;
const STRIDE = NODE_H + GAP;
// The not-accepted arm (Withdraw → …) sits this much further right than its lane slot so the wide
// "IF NOT ACCEPTED" edge label fits ON the connector between the await pill and the Withdraw node without
// overlapping either. Kept comfortably below LANE_W so the arm still stays inside its own branch lane.
const NOT_ACCEPTED_ARM_INSET = 90;
// Half-width (px) of the fixed "Withdraw invite" pill — the not-accepted arm's first node — used to center
// the edge label in the clear span between the await pill and Withdraw. (A fixed-content node, so a
// constant is safe here; nothing else depends on it.)
const WITHDRAW_HALF_W = 70;
// The canvas's first row. The builder puts the OWNER there (the sequence RUNS AS this person, above the
// automatic stages it drives); the locked per-contact view puts the folded "Prep" node there instead. The
// row is always occupied, so the geometry below stays plain constants rather than a function of props.
const TOP_ROW_Y = STRIDE;
// An extra half-stride of gap below the top row leaves room for the "Automatic · research & qualify" label
// between it and the first pre-step.
const PRE_TOP = TOP_ROW_Y + Math.round(STRIDE * 1.5); // center-y of the first pre-step
const preCenterY = (p: number) => PRE_TOP + p * STRIDE;
// First editable node center — an extra gap below the last pre-step ("Enroll") marks the handoff.
const EDIT_TOP = preCenterY(SEQUENCE_PRE_STEPS.length - 1) + Math.round(STRIDE * 1.5);
const nodeCenterY = (i: number) => EDIT_TOP + i * STRIDE;
const slotCenterY = (k: number) => EDIT_TOP - STRIDE / 2 + k * STRIDE;
// Half-width (px) reserved for the locked view's Prep pill — enough for the longest stage name it can
// carry. Only a reservation: the pill itself is sized by its text.
const PREP_NODE_RESERVE = 170;
// How much higher the LOCKED per-contact view draws everything from the top row down. It spends two fewer
// blocks than the builder: the owner (a property of the whole sequence — it rides on the sequence's pill
// there, not on the canvas) and the four separate automatic stages (folded into ONE "Prep" node). Sized so
// Prep lands on the top row with the SAME handoff gap to the first step, i.e. the collapsed graph opens on
// this contact's steps instead of on a screenful of empty canvas.
const COLLAPSED_LIFT = preCenterY(SEQUENCE_PRE_STEPS.length - 1) - TOP_ROW_Y;
// A switch fans its route branches DOWN below the node (the attribute toggle lives on the spine pill).
// These estimate a branch step's y (from the switch node's row), so the content editor opens beside the
// right row. The fan's actual width is shared (switchFanTotalWidth); column pitch is SWITCH_FAN_COL_W.
const SWITCH_BELOW = 18; // switch node CENTER → top of the branch fan (just below the pill's lower edge)
const SWITCH_HEADER = 96; // switch node row → a branch's first step (below-gap + connector band + chip)
const SWITCH_ROW = 76; // per additional branch step
// The FIRST range branch (column 1) sits ON the spine (under the node); the Everything-else slot is the
// one column to its LEFT — so the fan starts 1.5 columns left of CX.
const switchFanLeft = (cx: number): number => cx - 1.5 * SWITCH_FAN_COL_W;
// The fan's right edge in canvas x (0 when the step isn't a switch) — where the "+ range" tile ends.
const switchFanRight = (step: SequenceStep, cx: number): number =>
  step.type === 'switch' ? switchFanLeft(cx) + switchFanTotalWidth(step) : 0;

// A content step's authored text is previewed in the gutter to the RIGHT of its node (the people-piles
// sit on the left). Each preview hugs its OWN node's right edge (not the widest node on the spine) so
// the text sits right next to the step it belongs to — PREVIEW_GAP from it, PREVIEW_W wide (sequenceLayout).

/**
 * The DOM element the content editor anchors to for the open step. Re-derived LIVE from the step's ADDRESS
 * every time — never captured from whatever was last clicked — so the anchor is deterministic and can't
 * point at a stale/detached node after an interleaved edit. Every step on every surface (spine, spawn arm,
 * switch branch, group chip, and anything nested inside them) carries `data-line`/`data-idx`, so one query
 * reaches all of them; falls back to the containing node if the precise element can't be found.
 */
/**
 * Where a step's save tab slides out from: the step itself — or, for a chip inside a parallel group, the group,
 * since the group's box sits over its chips' layer and would hide a tab tucked behind one chip.
 */
function saveTabAddr(addr: StepAddr): StepAddr {
  const last = addr.line.path[addr.line.path.length - 1];
  if (last?.into !== 'group') return addr;
  return { line: { ...addr.line, path: addr.line.path.slice(0, -1) }, index: last.at };
}

function editorAnchorEl(addr: StepAddr, root: ParentNode | null): Element | null {
  const scope = root ?? document;
  const el = scope.querySelector(`[data-line="${serializeLine(addr.line)}"][data-idx="${addr.index}"]`);
  if (el != null) return el;
  // Fall back up the container chain — the outermost hop is always a spine node.
  const top = addr.line.path[0];
  return top == null ? null : scope.querySelector(`[data-node-index="${top.at}"]`);
}

// A node is dragged only once the pointer travels this many px from where it went down. Below it the
// gesture is treated as a click (→ open the step editor); at/above it the drag arms (ghost + drop).
const DRAG_THRESHOLD = 5;
/** How long "Saved" shows beside a step after its text saves. */
const SAVED_FLASH_MS = 1600;
/** Builder: the open step's text saves itself this long after the last keystroke. */
const AUTOSAVE_MS = 800;

/**
 * The per-contact content-override key for a step — the SAME key the engine computes from an arm's
 * `(path, cursor)` (see `sequenceOverrideKey`), so an edit here lands on the key the send actually reads.
 */
function addrToKey(addr: StepAddr): string {
  const { path, cursor } = lineStepCoords(addr.line, addr.index);
  return sequenceOverrideKey(path, cursor);
}

/** How many leading steps of a not-accepted arm are pinned (its auto `withdraw`, when it leads the arm). */
function pinnedArmHead(arm: SequenceStep[]): number {
  return arm[0]?.type === 'withdraw' ? 1 : 0;
}

/**
 * The graphical sequence editor on a pannable / zoomable canvas: step nodes centered on a vertical
 * SVG spine, with on-canvas "+" insertion points. A node is dragged by a cursor-tracking ghost — drop
 * it in a gap to reorder, or onto another basic step to combine the two into a parallel group (wait /
 * parallel-container nodes can only reorder). Every structural edit (insert / reorder / merge / remove
 * / wait duration) autosaves via `sequence.update` — no save button. Owns its editing state (seeded
 * from props at mount) so the post-save `get` refetch doesn't clobber in-flight edits.
 */
export function SequenceGraphEditor({
  teamId,
  sequenceId,
  initialSteps,
  owner,
  onChangeOwner,
  variables,
  documents,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  readOnly = false,
  overrides,
  stepStatus,
  onSaveStepContent,
  onCompleteManualStep,
  currentNodeId,
  history,
  fillsSection = false,
}: Props) {
  const utils = api.useUtils();
  const updateMutation = api.sequence.update.useMutation({
    // Every save sends the WHOLE step list, so saves must land in the order they were made: in one scope
    // React Query runs them one at a time, each after the last settles. Without it, adding a step (save 1:
    // the empty step) then writing its text and closing (save 2) can race — if save 1 is slower, it commits
    // last and the text is gone.
    scope: { id: `sequence.update:${sequenceId}` },
    // Each save's outcome is booked here — this runs for EVERY save, where a `mutate(…, { onSuccess })` runs
    // only for the latest call, so a later save would orphan an earlier one's "Saving…".
    onSuccess: (_data, variables) => {
      if (variables.steps != null) onStepsSaved(variables.steps);
      void utils.sequence.get.invalidate({ teamId, sequenceId });
      void utils.sequence.list.invalidate({ teamId });
      // Face piles are keyed by STEP ADDRESS, so inserting / deleting / reordering a step renumbers them:
      // without this the piles below the edit sit on the wrong node until the next 5s poll.
      void utils.sequence.faces.invalidate({ teamId, sequenceId });
    },
    onError: (_error, variables) => {
      if (variables.steps != null) onStepsSaveFailed(variables.steps);
      void utils.sequence.get.invalidate({ teamId, sequenceId });
    },
  });

  const [authoredSteps, setSteps] = useState<SequenceStep[]>(initialSteps);
  const stepsRef = useRef<SequenceStep[]>(initialSteps);
  // What the canvas draws AND validates. In the locked per-contact view that's THIS contact's content — each
  // saved override laid over its template step — because the override is what the engine validates and sends,
  // so the red ring, the preview and the editor all judge the text that will actually go out. Structure is
  // identical, so every address still resolves; edits read `stepsRef` (the template), never this.
  const steps = useMemo(
    () => (readOnly && overrides != null ? withContentOverrides(authoredSteps, overrides) : authoredSteps),
    [readOnly, overrides, authoredSteps],
  );
  const [pickerAt, setPickerAt] = useState<number | null>(null);
  // Hovering the "Click + to add your first step" hint animates the (separate) leading "+" as if hovered.
  const [firstStepHintHovered, setFirstStepHintHovered] = useState(false);
  const [dragSource, setDragSource] = useState<StepAddr | null>(null);
  const dragSourceRef = useRef<StepAddr | null>(null);
  // A gesture starts on pointer-down but only becomes a real drag once it crosses DRAG_THRESHOLD; until
  // then it's a potential click. `armed` gates every drag visual (ghost, grabbing cursor, drop preview,
  // dimming) so a plain click never flickers a drag.
  const [dragArmed, setDragArmed] = useState(false);
  const dragArmedRef = useRef(false);
  // Where the dragged step will land — held in a ref (read by the drop handler) since the drop feedback is
  // painted imperatively (screen-space, so it works in ANY surface without in-canvas layout math).
  const dropRef = useRef<DropTarget | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const pointerRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const dragStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  // The reorder insertion bar (a `fixed` element) + the element currently outlined as a merge target — both
  // positioned/styled imperatively in the drag move handler so they track any surface.
  const dropBarRef = useRef<HTMLDivElement | null>(null);
  const mergeElRef = useRef<HTMLElement | null>(null);
  // The drag hit-test measures every `[data-line]` element — scoped to THIS editor's own canvas, never the
  // whole document. The sequence route can have two graphs mounted at once (the editable one plus the
  // readOnly per-contact graph, opened as an overlay from a face), and they emit the SAME line addresses:
  // measuring `document` would merge both graphs' rects under one line key and slot drops against a
  // bounds-union spanning both.
  const canvasRef = useRef<HTMLDivElement | null>(null);
  // Which step's content editor is open — addressed exactly like a drag source, so any step on any
  // surface (spine, branch, spawn arm, group chip, nested) opens the same editor. Null = closed.
  const [editing, setEditing] = useState<StepAddr | null>(null);
  // Every save sends the WHOLE step list, and every edit swaps in a new array — so "is there anything to send"
  // and "is anything unsaved" are reference checks against these: the newest list handed to a save, and the
  // newest one the server has confirmed. One bookkeeping for every save (step text, structure, drops).
  const sentStepsRef = useRef<SequenceStep[]>(initialSteps);
  const savedStepsRef = useRef<SequenceStep[]>(initialSteps);
  // Builder: the pending autosave of the open step's text.
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Builder: the step editor's latest save, shown on its step (see SequenceStepSaveTab).
  const [stepSave, setStepSave] = useState<{ addr: StepAddr; status: StepSaveStatus } | null>(null);
  // The DOM element the content editor (a PORTALED Floating-UI popover) anchors to. Derived from `editing`
  // in the layout effect below — NOT captured from whatever was last clicked — so it's deterministic
  // (never null / stale / detached) and consistent: the same step opens the editor in the same place
  // whether you clicked its node, its content preview, or an arm chip.
  const [editorAnchor, setEditorAnchor] = useState<Element | null>(null);
  const editorFloat = useFloating({
    open: editing != null,
    placement: 'right-start',
    strategy: 'fixed',
    whileElementsMounted: (reference, floating, update) => autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: [offset(12), flip({ padding: 12 }), shift({ padding: 12 })],
    elements: { reference: editorAnchor },
  });
  // Anchor the editor to the open step, re-derived LIVE from `editing` via {@link editorAnchorEl} — a spine
  // step by its `data-node-index`, an arm step by its `data-line`/`data-idx` (or the Nth chip in its
  // container). useLayoutEffect so the anchor is in place before paint — no first-frame flash at the
  // viewport origin. No captured "last clicked" element: a query keyed on the address can't go stale or
  // detached when an interleaved edit re-renders the step, and it resolves the same however the editor was
  // opened (click, drop-to-edit, auto-open-on-insert, or a programmatic open).
  // Closing MUST clear the anchor, not just skip the lookup. `editorAnchor` is a live DOM Element held in
  // React state: leaving the last one in place pins that element after the editor closes, and once the step
  // it belonged to is reordered or deleted the node is detached from the document but still referenced —
  // an un-collectable subtree, one more retained on every open → edit-structure cycle.
  useLayoutEffect(() => {
    setEditorAnchor(editing == null ? null : editorAnchorEl(editing, canvasRef.current));
  }, [editing]);
  // readOnly mode: the in-progress per-contact body/subject edit for the open step (null → show the saved
  // override or the template). Committed to `onSaveStepContent` on close; the template `steps` never change.
  const [draftOverride, setDraftOverride] = useState<{ body?: string; subject?: string } | null>(null);
  // The group whose inline add-step picker is open — its wrapper is raised so the picker (rendered
  // inside that wrapper's stacking context) isn't painted over by the insert slots / faces.
  const [groupPickerOpen, setGroupPickerOpen] = useState<number | null>(null);
  // Same, for a switch's route-branch pickers — raise the switch wrapper while any branch picker is open.
  const [switchPickerOpen, setSwitchPickerOpen] = useState<number | null>(null);
  // A container (parallel group / spawn) just inserted from the "+" picker auto-opens its add-step
  // picker, so you land ready to add the first step — like a drag-merge lands you with steps already in
  // — instead of on an empty box. Holds that container's index until the freshly-mounted node consumes
  // it (cleared one-shot by the effect below).
  const [autoOpenContainerAt, setAutoOpenContainerAt] = useState<number | null>(null);
  const [expandedPreStep, setExpandedPreStep] = useState<string | null>(null);
  // Open picker for a not-accepted arm insertion point: { step: main index, arm: arm insertion index }.
  const [armPickerAt, setArmPickerAt] = useState<{ step: number; arm: number } | null>(null);
  const isArmPicker = (step: number, arm: number) => armPickerAt?.step === step && armPickerAt.arm === arm;

  useGraphRender('SequenceGraphEditor', () => ({
    steps: steps.length,
    editing: editing == null ? null : `${serializeLine(editing.line)}#${editing.index}`,
    dragging: dragSource == null ? null : `${serializeLine(dragSource.line)}#${dragSource.index}`,
    readOnly,
  }));

  const commitLocal = (next: SequenceStep[]) => {
    stepsRef.current = next;
    setSteps(next);
  };
  // Send `next` (the whole list). `announceAt` shows it on that step (the step editor's saves); a structural
  // save shows none and clears any tab, whose step may be about to move — it carries the text with it anyway.
  const saveSteps = (next: SequenceStep[], announceAt?: StepAddr) => {
    sentStepsRef.current = next;
    setStepSave(announceAt == null ? null : { addr: announceAt, status: 'saving' });
    updateMutation.mutate({ teamId, sequenceId, steps: next });
  };
  // Saves land in the order they were made (the mutation's scope), so each success is the newest confirmed.
  function onStepsSaved(saved: SequenceStep[]) {
    savedStepsRef.current = saved;
    if (saved === sentStepsRef.current)
      setStepSave((cur) => (cur?.status === 'saving' ? { ...cur, status: 'saved' } : cur));
  }
  // A failed newest save is no longer "sent": the next autosave / close sends it again, and the page keeps
  // asking before a reload until one lands. (An older one failing under a newer queued save is covered by it.)
  function onStepsSaveFailed(failed: SequenceStep[]) {
    if (failed !== sentStepsRef.current) return;
    sentStepsRef.current = savedStepsRef.current;
    setStepSave((cur) => (cur?.status === 'saving' ? { ...cur, status: 'error' } : cur));
  }
  const persistSteps = (next: SequenceStep[]) => {
    commitLocal(next);
    saveSteps(next);
  };

  // All structural edits read from stepsRef (the latest committed array) rather than the render-scope
  // `steps`, so an edit can never be computed from a stale snapshot mid drag-reorder.
  const insertAt = (index: number, type: SequenceStepType) => {
    // insertSpineStep applies creation defaults + auto-follow-ups (a connect trails an "Await accept").
    persistSteps(insertSpineStep(stepsRef.current, index, type));
    setPickerAt(null);
    // Steps you almost always write into open their editor immediately (see stepOpensEditorOnInsert).
    if (stepOpensEditorOnInsert(type)) setEditing({ line: SPINE, index });
  };

  // Insert a parallel container — a `group` seeded with two empty arms, a detached `spawn` with one, or a
  // `switch`. It lands ready to fill in: a group/spawn auto-opens its add-step picker, while a switch
  // auto-opens its first branch's RANGE editor (the range is what matters first) — see autoOpenContainerAt.
  const insertContainerAt = (index: number, type: SequenceContainerStepType) => {
    const step: SequenceStep =
      type === 'group'
        ? { type: 'group', arms: [[], []], join: 'all' }
        : type === 'switch'
          ? newSwitchStep()
          : { type: 'spawn', arm: [] };
    const next = stepsRef.current.slice();
    next.splice(index, 0, step);
    setPickerAt(null);
    persistSteps(next);
    setAutoOpenContainerAt(index);
  };

  const remove = (index: number) => persistSteps(stepsRef.current.filter((_, i) => i !== index));

  // A spawn's arm is a `branch` container line on the server (pg.sequence.service `normalizeStep`), so it
  // nests a group / spawn exactly like a switch branch does — the picker offers the same Parallel section.
  const insertSpawnArmContainer = (index: number, slot: number, type: SequenceContainerStepType) =>
    persistSteps(
      updateLine(stepsRef.current, { path: [{ at: index, into: 'spawn' }] }, (arr) => {
        const next = arr.slice();
        next.splice(slot, 0, newContainerStep(type));
        return next;
      }),
    );

  // Open the editor for a step inside a container nested in a switch branch — the container's own type
  // picks which of its lines the step lives in (a spawn's arm vs a group's chips).
  const openEditorForContainerArm = (switchIndex: number, branch: number, slot: number, arm: number) => {
    const branchLine: LineAddr = { path: [{ at: switchIndex, into: 'branch', index: branch }] };
    const container = stepAt(stepsRef.current, { line: branchLine, index: slot });
    if (container == null || (container.type !== 'spawn' && container.type !== 'group')) return;
    openEditor({ line: { path: [...branchLine.path, { at: slot, into: container.type === 'spawn' ? 'spawn' : 'group' }] }, index: arm });
  };

  const setWait = (index: number, waitDays: number, waitUnit: WaitUnit) => {
    persistSteps(stepsRef.current.map((s, i) => (i === index ? { ...s, waitDays, waitUnit } : s)));
  };
  const setDeadlineDays = (index: number, deadlineDays: number) => {
    persistSteps(stepsRef.current.map((s, i) => (i === index ? { ...s, deadlineDays } : s)));
  };

  // ── not-accepted arm (branches.notAccepted) ──
  const updateStepAt = (index: number, fn: (s: SequenceStep) => SequenceStep) =>
    persistSteps(stepsRef.current.map((s, i) => (i === index ? fn(s) : s)));
  // Create the arm, auto-seeded with a pinned Withdraw step. An arm authored elsewhere (a converted campaign
  // whose connect didn't auto-withdraw) may legitimately have no Withdraw — the pin is keyed on the step
  // TYPE at the head of the arm, never on "index 0".
  const addArm = (index: number) =>
    updateStepAt(index, (s) => ({ ...s, branches: { ...s.branches, notAccepted: [{ type: 'withdraw' }] } }));
  const removeArm = (index: number) => updateStepAt(index, (s) => ({ ...s, branches: undefined }));
  const insertArmStep = (index: number, armIndex: number, type: SequenceStepType) => {
    setArmPickerAt(null);
    updateStepAt(index, (s) => {
      const arm = (s.branches?.notAccepted ?? []).slice();
      // Never insert before the pinned Withdraw.
      const at = Math.max(pinnedArmHead(arm), armIndex);
      arm.splice(at, 0, newSequenceStep(type));
      return { ...s, branches: { ...s.branches, notAccepted: arm } };
    });
  };
  const removeArmStep = (index: number, armIndex: number) => {
    updateStepAt(index, (s) => {
      const arm = s.branches?.notAccepted ?? [];
      // The pinned Withdraw is non-removable — clearing the whole branch is `removeArm`.
      if (armIndex < pinnedArmHead(arm)) return s;
      const rest = arm.filter((_, i) => i !== armIndex);
      // Removing the last step of an unpinned arm removes the branch — an empty rail has no "+" to add
      // a step back and the engine would run it as a no-op.
      if (rest.length === 0) return { ...s, branches: undefined };
      return { ...s, branches: { ...s.branches, notAccepted: rest } };
    });
  };
  const setArmWait = (index: number, armIndex: number, waitDays: number, waitUnit: WaitUnit) =>
    updateStepAt(index, (s) => ({
      ...s,
      branches: {
        ...s.branches,
        notAccepted: (s.branches?.notAccepted ?? []).map((a, i) => (i === armIndex ? { ...a, waitDays, waitUnit } : a)),
      },
    }));
  const setArmDeadlineDays = (index: number, armIndex: number, deadlineDays: number) =>
    updateStepAt(index, (s) => ({
      ...s,
      branches: {
        ...s.branches,
        notAccepted: (s.branches?.notAccepted ?? []).map((a, i) => (i === armIndex ? { ...a, deadlineDays } : a)),
      },
    }));

  // One-shot: the freshly-mounted container's own mount effect opens its picker (child effects run
  // before this parent effect), so clear the flag right after to avoid a stale index later pointing at
  // the wrong node once steps are reordered.
  useEffect(() => {
    if (autoOpenContainerAt != null) setAutoOpenContainerAt(null);
  }, [autoOpenContainerAt]);

  // Save what the open editor holds (see below; set each render). Opening another step or closing goes
  // through it first, so switching steps — even by keyboard, with no click away — never drops an edit.
  const commitOpenEditorRef = useRef<() => void>(() => undefined);

  // Open a step's content editor. In readOnly (per-contact) mode only content SEND steps that aren't already
  // sent open — each open starts a fresh per-contact draft; the full editor opens any editable step.
  const openEditor = useCallback(
    (addr: StepAddr) => {
      if (!readOnly) {
        commitOpenEditorRef.current();
        setEditing(addr);
        return;
      }
      const step = stepAt(stepsRef.current, addr);
      if (step == null || !stepTakesContactOverride(step)) return;
      if (!isStepContentEditableNow(stepStatus?.[addrToKey(addr)])) return; // already run, or never will — locked
      commitOpenEditorRef.current();
      setDraftOverride(null);
      setEditing(addr);
    },
    [readOnly, stepStatus],
  );

  // ── step content editor (body / subject / like-count) ──
  const editingStep = editing == null ? null : stepAt(steps, editing);
  // For an email step, the nearest earlier email's subject on its OWN line — drives the editor's
  // "continue previous thread" toggle (null → first email, no toggle, own subject required).
  // A parallel group's chips all run at once, so no chip is "after" another and there is nothing to thread
  // onto — only an ordered line (spine / branch / spawn arm) has a previous email.
  const editingLineIsOrdered = editing != null && editing.line.path[editing.line.path.length - 1]?.into !== 'group';
  const editingPrevEmailSubject =
    editingStep?.type === 'email' && editing != null && editingLineIsOrdered
      ? previousEmailSubject(getLine(steps, editing.line), editing.index)
      : null;
  // In readOnly mode the editor shows THIS contact's content — the in-progress draft over `editingStep`
  // (already the saved override, else the template) — while the template itself stays untouched.
  const editorStep =
    editingStep == null || draftOverride == null
      ? editingStep
      : {
          ...editingStep,
          body: draftOverride.body ?? editingStep.body,
          subject: draftOverride.subject ?? editingStep.subject,
        };
  // The open step's problem, judged on exactly what the editor shows right now in the context of its line —
  // the same check that rings the node — so the editor says WHY the node is red and clears as the rep fixes it.
  const editorIssue =
    editing == null || editorStep == null
      ? null
      : sequenceStepIssue(
          getLine(steps, editing.line).map((s, i) => (i === editing.index ? editorStep : s)),
          editing.index,
        );
  // Builder: save the step editor's edits now — whatever hasn't been sent, if anything — shown on its step.
  // Cancels a pending autosave (this IS that save, sooner). Reads the newest state through refs, so the
  // timers and listeners that call it never go stale.
  const saveEditorEdits = (addr: StepAddr) => {
    if (autosaveTimerRef.current != null) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    if (stepsRef.current === sentStepsRef.current) return;
    saveSteps(stepsRef.current, addr);
  };
  const saveEditorEditsRef = useRef(saveEditorEdits);
  useEffect(() => {
    saveEditorEditsRef.current = saveEditorEdits;
    commitOpenEditorRef.current = () => {
      if (editing == null) return;
      if (!readOnly) {
        saveEditorEdits(editing);
        return;
      }
      // Persist the per-contact override (only if it changed) — never the template.
      if (draftOverride != null && onSaveStepContent != null) void onSaveStepContent(addrToKey(editing), draftOverride);
      setDraftOverride(null);
    };
  });
  // Leaving with an autosave still pending (a back-button, a route change) sends it rather than dropping the
  // last keystrokes.
  useEffect(() => () => commitOpenEditorRef.current(), []);
  // Reloading or closing the tab with an edit not yet saved would lose it — anything the server hasn't
  // confirmed (an autosave not yet fired, a save on its way, one that failed), or a contact's copy still open
  // with changes. Ask first.
  useBeforeUnloadGuard(() => (readOnly ? draftOverride != null : stepsRef.current !== savedStepsRef.current));

  // Live field edits update local state at once (smooth typing). Builder: the text then saves itself a moment
  // after the last keystroke, the editor staying open; closing saves anything still pending. readOnly: the edit
  // is a per-contact override tracked separately and saved on close — the template never changes.
  const updateEditingStepLocal = (next: SequenceStep) => {
    if (editing == null) return;
    if (readOnly) {
      setDraftOverride({ body: next.body, subject: next.subject });
      return;
    }
    // One address → one write path: `updateLine` reaches ANY line (spine, spawn arm, switch branch, group
    // chips, nested) — the per-shape traversals this used to hand-roll were the same walk, five times over.
    commitLocal(updateLine(stepsRef.current, editing.line, (arr) => arr.map((s, i) => (i === editing.index ? next : s))));
    const addr = editing;
    if (autosaveTimerRef.current != null) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      saveEditorEditsRef.current(addr);
    }, AUTOSAVE_MS);
  };
  // Save what's open and close it — ⌘/Ctrl+Enter (the text fields' submit), the ✕, Escape, a click away.
  // Stable, so the listeners below and the editor's submit binding (captured once) never go stale.
  const closeEditor = useCallback(() => {
    commitOpenEditorRef.current();
    setEditing(null);
  }, []);
  // Escape with focus outside the card's fields, through the shared esc stack — above the contact panel's own
  // handler, so in the per-contact view it closes the editor, not the panel around it. (Escape inside a field
  // is the editor card's own, see SequenceStepEditor.)
  useEscStack('SEQUENCE_STEP_EDITOR', ESC_PRIORITY.INTERSTITIAL, editing != null ? closeEditor : undefined);
  // A click outside the editor card saves and closes it.
  useEffect(() => {
    if (editing == null) return;
    const onDown = (e: PointerEvent) => {
      // The mention dropdowns (@ variables / / documents / { comments) portal outside the card as
      // `.tippy-box` popups, and the reminder-action dropdown renders in a native `[popover]` top-layer —
      // a click in either is part of editing (picking a mention / a reminder), not a click-away, so keep
      // the editor open.
      if (
        e.target instanceof Element &&
        e.target.closest('[data-step-editor]') == null &&
        e.target.closest('.tippy-box') == null &&
        e.target.closest('[popover]') == null
      )
        closeEditor();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [editing, closeEditor]);

  // A "Saved" settles back to nothing after a moment; "Saving…" and "Couldn't save" stay until they change.
  useEffect(() => {
    if (stepSave?.status !== 'saved') return;
    const t = setTimeout(() => setStepSave((cur) => (cur === stepSave ? null : cur)), SAVED_FLASH_MS);
    return () => clearTimeout(t);
  }, [stepSave]);
  // The save tab slides out from behind the step it's about (the same element the editor anchors to): where
  // that step's tile sits in canvas content px, so the tab lives in the canvas, a layer under the node.
  const [stepSavePos, setStepSavePos] = useState<{ x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const node = stepSave == null ? null : editorAnchorEl(saveTabAddr(stepSave.addr), canvas);
    if (canvas == null || node == null) {
      setStepSavePos(null);
      return;
    }
    const c = canvas.getBoundingClientRect();
    const r = node.getBoundingClientRect();
    // Screen px → content px: the canvas is drawn scaled by the current zoom.
    const scale = canvas.offsetWidth > 0 ? c.width / canvas.offsetWidth : 1;
    setStepSavePos({ x: (r.left + r.width / 2 - c.left) / scale, y: (r.top + r.height / 2 - c.top) / scale });
  }, [stepSave]);

  // Close the step picker (main spine or arm) on Escape or a click/pointer outside it.
  useEffect(() => {
    if (pickerAt == null && armPickerAt == null) return;
    const closeAll = () => {
      setPickerAt(null);
      setArmPickerAt(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAll();
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-sequence-picker]') == null) closeAll();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [pickerAt, armPickerAt]);

  // Close an expanded pre-step on Escape or a click/pointer outside it.
  useEffect(() => {
    if (expandedPreStep == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpandedPreStep(null);
    };
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('[data-prestep]') == null) setExpandedPreStep(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown, true);
    };
  }, [expandedPreStep]);

  const beginDrag = (source: StepAddr, e: { clientX: number; clientY: number }) => {
    dragSourceRef.current = source;
    pointerRef.current = { x: e.clientX, y: e.clientY };
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    dropRef.current = null;
    dragArmedRef.current = false;
    setDragArmed(false);
    setDragSource(source);
  };
  // Open the content editor for a line step at `addr` (spine / switch branch / spawn arm) — mirrors the
  // spine's click-to-edit so a branch step opens the SAME editor, anchored to itself.
  const openEditorForAddr = useCallback(
    (addr: StepAddr) => {
      const step = stepAt(stepsRef.current, addr);
      if (step == null || !stepIsEditable(step)) return;
      openEditor(addr);
    },
    [openEditor],
  );
  // Any LINE step (spine / switch branch / spawn arm) is a drag source: below the threshold it's a click
  // that opens the editor, past it a cross-surface move. Locked view: click-to-edit only (structure frozen).
  const startStepDrag = (addr: StepAddr, e: React.PointerEvent) => {
    // The surface that owns the pointerdown has already claimed the gesture (beginStepGesture); all that's
    // left is what it means here. Locked view: click-to-edit only (structure frozen).
    if (readOnly) {
      openEditorForAddr(addr);
      return;
    }
    beginDrag(addr, e);
  };
  const startDrag = (e: React.PointerEvent, index: number) => {
    if (!beginStepGesture(e)) return;
    startStepDrag({ line: SPINE, index }, e);
  };
  // Clear the imperative drop feedback (insertion bar + merge outline).
  const clearDropFeedback = () => {
    if (dropBarRef.current != null) dropBarRef.current.style.display = 'none';
    if (mergeElRef.current != null) {
      mergeElRef.current.style.outline = '';
      mergeElRef.current.style.outlineOffset = '';
      mergeElRef.current = null;
    }
  };

  // Drag: a ghost tracks the cursor (screen-space); the drop target is computed on move by hit-testing
  // EVERY line on the canvas. Each line tags its steps with `data-line`/`data-idx` (its absolute address)
  // and, when empty, a `data-empty` anchor rect — so a step can be reordered within its line, moved into
  // ANY other line at any depth (spine ↔ switch branch ↔ spawn arm ↔ group chips, nested), or merged onto
  // a node to form/join a parallel group. There is no separate "arm extract" path: a group's chips are a
  // line like any other. Nothing mutates `steps` mid-drag — the move (sequenceMove.moveStep) is applied
  // once on drop; Escape cancels. Drop feedback is painted imperatively.
  // Applying a drop needs the newest mutation + editor-opener, but the drag's window listeners must NOT
  // be torn down and rebound to get them: React Query hands back a NEW `updateMutation` object on every
  // render, so depending on it rebinds three window listeners AND rewrites document.body's style (a
  // whole-document style invalidation) on every single render that lands mid-gesture. Route the moving
  // parts through a ref instead, and let the effect depend only on whether a drag is in flight.
  const applyDropRef = useRef<(src: StepAddr | null, target: DropTarget | null) => void>(() => undefined);
  useEffect(() => {
    applyDropRef.current = (src, target) => {
      if (dragArmedRef.current) {
        if (src == null || target == null) return;
        const next = moveStep(stepsRef.current, src, target);
        // Skip a no-op drop (moveStep returns the same array): no local churn, no wasted save.
        if (next === stepsRef.current) return;
        commitLocal(next);
        saveSteps(next);
        return;
      }
      // A click (never crossed the threshold) opens the step's editor, if it has content to edit.
      if (src != null) openEditorForAddr(src);
    };
  });

  const dragActive = dragSource !== null;
  useEffect(() => {
    if (!dragActive) return;
    // Belt and braces to the per-gesture preventDefault: while a drag is in flight nothing on the page may
    // accumulate a selection, including text the cursor sweeps outside the canvas. Restored on drop/cancel.
    seqLog('drag listeners BIND');
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    const onMove = (e: PointerEvent) => {
      const src = dragSourceRef.current;
      if (src == null) return;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      // Below the threshold the gesture is still a potential click — don't arm the drag yet.
      if (!dragArmedRef.current) {
        const moved = Math.hypot(e.clientX - dragStartRef.current.x, e.clientY - dragStartRef.current.y);
        if (moved < DRAG_THRESHOLD) return;
        dragArmedRef.current = true;
        setDragArmed(true);
      }
      if (ghostRef.current != null)
        ghostRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -50%)`;

      // Where does this gesture want to land? Geometry lives in sequenceDrop so it can be tested against
      // a real rendered surface; here we just paint the answer.
      const res = resolveDrop(readLineRects(canvasRef.current ?? document), { x: e.clientX, y: e.clientY }, src, stepsRef.current);
      dropRef.current = res?.drop ?? null;

      // Imperative feedback: outline a merge target, else show the reorder insertion bar (screen-space).
      const overEl = res?.mergeEl ?? null;
      if (mergeElRef.current != null && mergeElRef.current !== overEl) {
        mergeElRef.current.style.outline = '';
        mergeElRef.current.style.outlineOffset = '';
        mergeElRef.current = null;
      }
      if (overEl != null) {
        overEl.style.outline = '2px solid rgb(139 92 246)'; // violet-500
        overEl.style.outlineOffset = '3px';
        mergeElRef.current = overEl;
      }
      const bar = res?.bar ?? null;
      if (dropBarRef.current != null) {
        if (bar != null) {
          dropBarRef.current.style.display = 'block';
          dropBarRef.current.style.top = `${bar.top}px`;
          dropBarRef.current.style.left = `${bar.left}px`;
          dropBarRef.current.style.width = bar.width != null ? `${bar.width}px` : '2px';
          dropBarRef.current.style.height = bar.height != null ? `${bar.height}px` : '2px';
        } else {
          dropBarRef.current.style.display = 'none';
        }
      }
    };
    const onUp = () => {
      const src = dragSourceRef.current;
      const target = dropRef.current;
      try {
        applyDropRef.current(src, target);
      } finally {
        // ALWAYS tear the gesture down. If applying the drop throws, the drag must still end — otherwise
        // `dragArmed` stays true, the canvas keeps `pointer-events-none`, the ghost stays glued to the
        // cursor, and the whole page reads as frozen.
        cancelDrag();
      }
    };
    const cancelDrag = () => {
      clearDropFeedback();
      dragSourceRef.current = null;
      dropRef.current = null;
      dragArmedRef.current = false;
      setDragArmed(false);
      setDragSource(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelDrag();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      seqLog('drag listeners RELEASE');
      document.body.style.userSelect = prevUserSelect;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('keydown', onKey);
    };
    // ONE bind per gesture: everything that changes per render is reached through applyDropRef.
  }, [dragActive]);

  // Faces per node — how many enrolled people sit on each graph node + a small avatar sample. Polls so
  // the piles follow contacts as they advance through the sequence (shared with the full journey graph).
  const facesQuery = useSequenceFaces(teamId, sequenceId, !readOnly);
  // Memoized (like the full journey graph) so the fallback doesn't mint a fresh object each render — a live
  // poll that returns unchanged data then can't churn the canvas. The locked per-contact graph reads NOTHING
  // here: the query above is disabled for it, but React Query still hands back cached data whenever the
  // team-wide editor is mounted behind it (same key), which would spill the whole team's piles onto a
  // one-person graph. That view marks the ONE node this contact is on instead (SequenceCurrentStepMarker).
  const byNode = useMemo(
    () => (readOnly ? NO_FACES : (facesQuery.data?.byNode ?? NO_FACES)),
    [readOnly, facesQuery.data],
  );
  // People per AUTHORED STEP, keyed by step address then by the node they rest on. The STAGE piles read
  // `byNode` (a stage folds several nodes, each of which exists once); the STEP piles must not — a node id
  // says "a wait" but not WHICH wait, so a by-node pile on the authored graph repeats itself on every step
  // of that type and reads as contacts having walked past steps they're still parked before.
  const byStep = useMemo(
    () => (readOnly ? NO_STEP_FACES : (facesQuery.data?.byStep ?? NO_STEP_FACES)),
    [readOnly, facesQuery.data],
  );
  const sumFaces = (groups: { count: number; sample: SequenceFace[] }[]): { total: number; sample: SequenceFace[] } => {
    let total = 0;
    const sample: SequenceFace[] = [];
    for (const f of groups) {
      total += f.count;
      for (const s of f.sample) if (sample.length < 6) sample.push(s);
    }
    return { total, sample };
  };
  /** Everyone folded into a STAGE (or the Finished terminal) — several journey nodes, summed. */
  const facesFor = (nodeIds: string[]) => sumFaces(nodeIds.flatMap((id) => byNode[id] ?? []));
  /** Everyone standing on ONE authored step, across every node that step can park them on. */
  const facesForStep = (path: SequenceArmPathSeg[], index: number) =>
    sumFaces(Object.values(byStep[sequenceOverrideKey(path, index)] ?? {}));

  // Per-face actions (open detail in place / restart / remove / mark a call·reminder to-do done) — shared
  // with the full journey graph.
  const {
    onOpenContact: onOpenContactFace,
    onRestart: onRestartFace,
    onRemove: onRemoveFace,
    onCompleteTask: onCompleteTaskFace,
  } = useSequenceFaceActions(teamId, sequenceId);

  // Every journey node the authored graph already pins a face onto: the pre-step folds plus each present
  // step's own node + its park/wait lanes (recursively — not-accepted arms, parallel + spawned + switch
  // branches). Anyone on a node OUTSIDE this set is at risk of being silently dropped from the canvas.
  //
  // Deliberately BY NODE, even though the piles are now placed by step address. The two answer different
  // questions: `byStep` says WHICH step holds a person, this says whether the canvas has any step that
  // COULD. Subtracting the placed (per-ARM) counts from `byNode`'s (per-CONTACT) counts instead would
  // report a false orphan for every contact whose enrollment node is a stale root mirror — the engine
  // only mirrors `flow.enrollment.current_node_id` from the ROOT arm, so a contact running a not-accepted
  // branch still reads as sitting on `seq.await_connect` while their live arm is drawn on the branch.
  const claimedNodeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of Object.values(PRE_STEP_NODE_IDS)) for (const id of group) ids.add(id);
    for (const id of SEQUENCE_FINISHED_NODE_IDS) ids.add(id); // the spine's closing Finished node
    const walk = (list: SequenceStep[]) => {
      for (const s of list) {
        for (const id of stepFaceNodeIds(s.type)) ids.add(id);
        if (s.branches?.notAccepted != null) walk(s.branches.notAccepted);
        if (s.arms != null) for (const a of s.arms) walk(a);
        if (s.arm != null) walk(s.arm);
        if (s.switch != null) for (const b of s.switch.branches) walk(b.arm);
      }
    };
    walk(steps);
    return ids;
  }, [steps]);

  // Safety net (mirrors the full journey graph's "Other states" pile): anyone resting on a node the
  // authored graph doesn't claim — a shared park lane (off-hours wait, reconnect LinkedIn, needs
  // attention, …) or an unmodelled/renamed node — folds into ONE visible pile so no enrolled contact is
  // ever silently hidden. Terminal SUCCESS states (finished / responded) are skipped so a long-running
  // sequence's completed contacts don't clutter it — those are an outcome, not an open task.
  const offGraph = useMemo(
    () => otherStatesFrom(byNode, (nodeId) => !claimedNodeIds.has(nodeId) && !isJourneyDoneNode(nodeId)),
    [byNode, claimedNodeIds],
  );

  const n = steps.length;
  // Vertical origin. The locked per-contact view lifts the whole spine (see COLLAPSED_LIFT); the builder
  // is unshifted. Every y in the render goes through these, so the two surfaces can't drift apart.
  const lift = readOnly ? COLLAPSED_LIFT : 0;
  const nodeY = (i: number) => nodeCenterY(i) - lift;
  const slotY = (k: number) => slotCenterY(k) - lift;
  // Where this contact actually is. `stepStatus` alone can't say: an arm is inserted at cursor 0 the moment
  // they're enrolled, so it reports step 0 as `current` all through the automatic front-half — long before
  // anything has been sent. `currentNodeId` is the tiebreaker, and while it's a prep node the Prep node
  // owns the "now" marker and no authored step claims it.
  const inPrep = readOnly && currentNodeId != null && isPrepNode(currentNodeId);
  const statusAt = (key: string): SequenceContactStepStatus | undefined => contactStepStatus(stepStatus?.[key], inPrep);
  // Every step's painted state, derived ONCE per render: the node map, the content previews, the gutter
  // marker, the progress line and each arm all ask for the same keys, and each derivation runs several
  // tailwind-merges. Empty in the builder, which has no one contact to paint for.
  const visuals = useMemo(() => {
    const map = new Map<string, SequenceStepVisual | null>();
    if (!readOnly || stepStatus == null) return map;
    for (const [key, status] of Object.entries(stepStatus))
      map.set(key, sequenceStepVisual(contactStepStatus(status, inPrep)));
    return map;
  }, [readOnly, stepStatus, inPrep]);
  /** How a step should be painted for this contact — null in the builder, which has no one contact. */
  const visualAt = (key: string): SequenceStepVisual | null => visuals.get(key) ?? null;
  // The Prep node borrows the step tones: a contact parked on a blocked/dead prep stage gets the same amber
  // or red halo a blocked/dead STEP would, rather than a blue "running" one around an amber pill.
  const prepVisual =
    inPrep && currentNodeId != null ? sequenceStepVisual({ state: 'current', nodeId: currentNodeId }) : null;
  /** The not-accepted arm's own key space — the engine walks it as its own path (`[step, 'notAccepted']`). */
  const armVisualAt = (stepIndex: number, armIndex: number): SequenceStepVisual | null =>
    visualAt(sequenceOverrideKey([stepIndex, NOT_ACCEPTED_SEG], armIndex));
  // How far down the spine this contact has actually got: the last step they've run or are resting on. The
  // connector is drawn solid to there and dashed beyond, so "how far along are they" reads at a glance
  // without inspecting a single node. -1 (nothing started) leaves the whole run dashed.
  const lastReachedIndex = readOnly
    ? steps.reduce((last, _step, i) => {
        const state = statusAt(sequenceOverrideKey([], i))?.state;
        // `skipped` counts: the arm ran past it (it just didn't need to do anything), so the progress line
        // must reach it — otherwise an already-connected contact's spine draws as if nothing had started.
        return state === 'done' || state === 'skipped' || state === 'current' || state === 'errored' ? i : last;
      }, -1)
    : steps.length - 1;
  // A switch's fan hangs BELOW the node and spreads RIGHT — its "+ range" column can reach well past any
  // node. A spawn / not-accepted lane runs vertically DOWN past the switch's row, so the lanes must start
  // clear of the widest switch fan's right edge, not just the widest spine node (else the lane's nodes
  // overlap the fan). Fed to computeBranchLanes below.
  const maxSwitchFanRight = steps.reduce((m, s) => Math.max(m, switchFanRight(s, CX)), 0);
  // Branch layout (pure + recomputed each render, so it adapts as steps change): each side branch — a
  // spawn's "runs alongside" box or an await's not-accepted arm — gets its OWN vertical lane to the right
  // of the WIDEST node (and any switch fan), so nothing overlaps whatever the node widths are. See
  // {@link computeBranchLanes}.
  const lanes = computeBranchLanes(steps, CX, maxSwitchFanRight);
  const branchXFor = lanes.branchXFor;
  // The not-accepted arm lives in the step's branch lane, nudged right (NOT_ACCEPTED_ARM_INSET) so its wide
  // "IF NOT ACCEPTED" edge label fits on the connector between the pills.
  const armXFor = (i: number) => lanes.branchXFor(i) + NOT_ACCEPTED_ARM_INSET;
  // The connector leaves each node at its actual right edge (a wide pill's edge, not a fixed offset).
  const nodeRightXFor = (i: number) => CX + spineNodeHalfWidth(steps[i] ?? { type: 'follow' });
  // Where the spine's solid run ENDS. The line exists to reach the trailing "+" slot below the last step —
  // that's the affordance it connects to. Two surfaces have no such slot: the locked per-contact view (no
  // inserts at all) and a spine ending in a switch (terminal, so the slot below it is deliberately
  // suppressed — see the slot map). Without one, the line is a stub pointing at nothing, so it ends ON the
  // last node instead of dangling half a stride past it (into the switch's own branch fan, in that case).
  // A switch ENDS the main line — its route branches are terminal paths that fan DOWN the spine — so
  // nothing may be drawn in the rows below it (see the suppressed trailing "+" and the Finished node).
  const endsInSwitch = steps[n - 1]?.type === 'switch';
  // The spine now closes on the Finished node (a step's arrow leads somewhere), except after a switch,
  // where the line stops on the switch itself and the fan takes over.
  const spineEndY = endsInSwitch ? nodeY(n - 1) : nodeY(Math.max(n, 1));

  // Height reservation stays keyed to the SLOT, not the (possibly shorter) line: the last step's content
  // preview is centered on its node and needs the room below it whether or not a "+" is drawn there.
  let maxBottom = slotY(n) + STRIDE;
  // The locked view's Prep pill carries the live stage name ("Prep · Linkedin disconnected"), so it can be
  // wider than any node on the spine. Reserve room for it or the canvas box clips it and "fit to screen"
  // (which reads scrollWidth) opens on a graph that runs off its own right edge.
  let maxRight = Math.max(lanes.contentRight(CX), readOnly ? CX + PREP_NODE_RESERVE : 0);
  steps.forEach((s, i) => {
    if (s.type === 'await_connect') {
      const armLen = s.branches?.notAccepted?.length ?? 0;
      if (armLen > 0) maxBottom = Math.max(maxBottom, nodeY(i) + armLen * STRIDE);
    }
    // The spawn box is a full vertical sub-line centered on the node — reserve ~half its height below.
    if (s.type === 'spawn') {
      const armLen = s.arm?.length ?? 0;
      maxBottom = Math.max(maxBottom, nodeY(i) + 60 + armLen * 44);
    }
    // The switch fan hangs BELOW the node, centered on the spine (column 0) and fanning RIGHT — reserve
    // the fan's width (from the spine) and the deepest branch's depth.
    if (s.type === 'switch') {
      // A branch's vertical extent is its own steps PLUS the tallest spawn arm flaired off it (which drops
      // down alongside the branch line) — so a fit / canvas reservation doesn't clip a long parallel arm.
      const deepest = (s.switch?.branches ?? []).reduce((m, b) => {
        const tallestSpawn = b.arm.reduce((mm, st) => Math.max(mm, st.type === 'spawn' ? (st.arm?.length ?? 0) : 0), 0);
        return Math.max(m, b.arm.length + tallestSpawn);
      }, 0);
      maxRight = Math.max(maxRight, switchFanRight(s, CX) + 40);
      maxBottom = Math.max(maxBottom, nodeY(i) + SWITCH_HEADER + deepest * SWITCH_ROW + 60);
    }
  });
  // Everyone who ran off the end of the steps rests on that terminal. No authored step claims them and the
  // "Other states" pile deliberately skips done terminals, so this node is their only home on the canvas.
  const finishedFaces = facesFor(SEQUENCE_FINISHED_NODE_IDS);
  // An EMPTY sequence can still have finished contacts — enrolling into one runs straight off the end of
  // zero steps, and a rep can delete every step from a sequence people already completed. So the terminal
  // is drawn whenever someone is actually resting on it, not just when there are steps to precede it.
  const showFinished = n > 0 || finishedFaces.total > 0;
  // Where the journey ENDS. One row past the trailing "+" — or, when the spine ends in a switch (whose
  // branches fan DOWN and own every row below the node), clear of the whole fan, since the main line has
  // already terminated on the switch and no connector runs down to it. With no steps at all it drops a row
  // further so it can't land on the "add your first step" hint.
  const finishedY = endsInSwitch ? maxBottom + STRIDE / 2 : nodeY(Math.max(n, 1));
  // Where "now" is on the locked canvas — what the full-section Graph view opens on and its "Now" control
  // returns to: Prep while they're in it, else the step they're on, else Finished, else as far as they got.
  const hereIndex = steps.findIndex((_, i) => visualAt(sequenceOverrideKey([], i))?.isHere === true);
  const focusY = inPrep
    ? TOP_ROW_Y
    : hereIndex >= 0
      ? nodeY(hereIndex)
      : currentNodeId != null && SEQUENCE_FINISHED_NODE_IDS.includes(currentNodeId)
        ? finishedY
        : lastReachedIndex >= 0
          ? nodeY(lastReachedIndex)
          : TOP_ROW_Y;
  if (showFinished) maxBottom = Math.max(maxBottom, finishedY + STRIDE);

  // Content previews sit in the right gutter, each hugging its own node's right edge, capped so they
  // never poke into the branch lanes. Reserve room so the overflow-hidden canvas can't clip them.
  const previewLeftFor = (i: number) => nodeRightXFor(i) + PREVIEW_GAP;
  const previewMaxWidthFor = (i: number) =>
    lanes.laneCount > 0 ? Math.max(120, lanes.laneX0 - previewLeftFor(i) - 16) : PREVIEW_W;
  steps.forEach((s, i) => {
    if (stepHasContentPreview(s)) maxRight = Math.max(maxRight, previewLeftFor(i) + previewMaxWidthFor(i) + 24);
  });

  // The content editor is a portaled popover anchored (Floating UI) to the clicked node's element — no
  // in-canvas coordinate math, no reserved space; it can't be clipped and doesn't scale with zoom.
  // The step pickers are portaled to the body and floated by Floating UI (see SequenceGraphInsert), so
  // they no longer need reserved canvas space to avoid the overflow-hidden clip.
  const contentWidth = maxRight;
  const contentHeight = maxBottom;
  const expandedIndex = SEQUENCE_PRE_STEPS.findIndex((s) => s.id === expandedPreStep);
  const anyPickerOpen = pickerAt !== null || armPickerAt !== null;
  // Only reflect drag state once the gesture is armed — an un-armed gesture is still a pending click. Dims
  // the dragged SPINE node (branch steps dim via their own surface; the ghost is the main cue).
  const dragSpineIndex = dragArmed && dragSource != null && dragSource.line.path.length === 0 ? dragSource.index : null;
  const draggedStep = dragSource == null ? null : stepAt(steps, dragSource);

  // Room on the left for the gutter's markers in the full-section view (see FILL_LEFT_GUTTER).
  const leftGutter = fillsSection ? FILL_LEFT_GUTTER : 0;
  return (
    <div className={cn('relative size-full', dragArmed && 'cursor-grabbing')}>
      {/* The locked view is embedded in the contact detail page's scrolling panel, so it must NOT swallow
          the wheel — otherwise the canvas becomes a scroll trap and the sections below it are unreachable.
          The builder owns its whole page, where wheel-to-pan is the point. */}
      <SequencePanZoom
        wheelPan={!readOnly || fillsSection}
        fitOnMount={readOnly && !fillsSection}
        focusSelector={fillsSection ? '[data-canvas-focus]' : undefined}
        boundPan={fillsSection}>
        {readOnly && history != null && history.items.length > 0 && (
          <SequenceHistoryColumn
            items={history.items}
            actions={history.actions}
            hasOlder={history.hasOlder}
            loadingOlder={history.loadingOlder}
            olderFailed={history.olderFailed}
            onLoadOlder={history.onLoadOlder}
            spineX={CX + leftGutter}
            width={contentWidth + leftGutter}
          />
        )}
        {/* While dragging, kill pointer events on the canvas so passing over nodes doesn't fire their
          hover affordances (remove buttons, tooltips, the growing "+") and obscure the drop. The drop
          hit-test reads getBoundingClientRect + window listeners, so it's unaffected. */}
        <div
          ref={canvasRef}
          className={cn('relative', dragArmed && 'cursor-grabbing [&_*]:pointer-events-none')}
          style={{ width: contentWidth, height: contentHeight, marginLeft: leftGutter }}>
          {fillsSection && (
            <span
              aria-hidden
              data-canvas-focus
              className="pointer-events-none absolute size-px"
              style={{ left: CX, top: focusY }}
            />
          )}
          <svg width={contentWidth} height={contentHeight} className="absolute left-0 top-0 overflow-visible">
            {/* The history above flows straight into Prep: carry its (solid — it happened) spine down to it. */}
            {readOnly && history != null && history.items.length > 0 && (
              <line
                x1={CX}
                y1={0}
                x2={CX}
                y2={TOP_ROW_Y}
                className="stroke-lynch-300 dark:stroke-slate-600"
                strokeWidth={2}
              />
            )}
            {/* Dashed from the owner down through the automatic front-half, solid through the editable steps. */}
            <line
              x1={CX}
              y1={TOP_ROW_Y}
              x2={CX}
              y2={slotY(0)}
              className="stroke-lynch-300 dark:stroke-slate-600"
              strokeWidth={2}
              strokeDasharray="4 5"
            />
            {n > 0 && (
              <Fragment>
                {/* Solid through what has already happened… */}
                {lastReachedIndex >= 0 && (
                  <line
                    x1={CX}
                    y1={slotY(0)}
                    x2={CX}
                    y2={Math.min(nodeY(lastReachedIndex), spineEndY)}
                    className="stroke-lynch-300 dark:stroke-slate-600"
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                )}
                {/* …dashed through what hasn't (in the locked view; the builder's line is solid throughout,
                  since there's no one contact whose progress it could show). */}
                {(!readOnly || lastReachedIndex < n - 1) && (
                  <line
                    x1={CX}
                    y1={lastReachedIndex >= 0 ? Math.min(nodeY(lastReachedIndex), spineEndY) : slotY(0)}
                    x2={CX}
                    y2={spineEndY}
                    className={cn(
                      readOnly ? 'stroke-lynch-200 dark:stroke-slate-700' : 'stroke-lynch-300 dark:stroke-slate-600',
                    )}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeDasharray={readOnly ? '4 5' : undefined}
                  />
                )}
              </Fragment>
            )}
            {expandedIndex >= 0 && (
              <line
                x1={CX + 58}
                y1={preCenterY(expandedIndex)}
                x2={CX + 96}
                y2={preCenterY(expandedIndex)}
                className="stroke-blue-400"
                strokeWidth={2}
              />
            )}
            {/* not-accepted arm connectors (rose = the negative branch) */}
            {steps.map((step, i) => {
              const arm = step.branches?.notAccepted;
              if (step.type !== 'await_connect' || arm == null || arm.length === 0) return null;
              const y0 = nodeY(i);
              const ax = armXFor(i);
              // A lane this contact will never walk (they accepted) is drawn cold + dashed, so the whole
              // branch recedes instead of advertising sends that can't happen.
              const armDead = armVisualAt(i, 0)?.tone === 'notTaken';
              const armStroke = armDead
                ? 'stroke-lynch-200 dark:stroke-slate-700'
                : 'stroke-rose-300 dark:stroke-rose-500/60';
              // The builder runs the lane half a row past its last step, down to the "+" that appends one. The
              // locked view has no "+", so the lane stops AT its last step rather than trailing into nothing.
              const armEndY = y0 + (arm.length - 1) * STRIDE + (readOnly ? 0 : STRIDE / 2);
              return (
                <Fragment key={`arm-line-${i}`}>
                  {/* Start at the node CENTER, not the estimated right edge: the opaque pill paints over the
                    SVG, so the line stays hidden under the pill and emerges exactly at its real right edge —
                    always connected, regardless of how wide the "Await accept · up to N days" pill renders. */}
                  <path
                    d={`M ${CX} ${y0} H ${ax}`}
                    className={armStroke}
                    strokeWidth={2}
                    fill="none"
                    strokeDasharray={armDead ? '4 5' : undefined}
                  />
                  {armEndY > y0 && (
                    <line
                      data-arm-lane={i}
                      x1={ax}
                      y1={y0}
                      x2={ax}
                      y2={armEndY}
                      className={armStroke}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeDasharray={armDead ? '4 5' : undefined}
                    />
                  )}
                </Fragment>
              );
            })}
            {/* spawn side-branch: a connector from the node's right edge across to its "runs alongside"
              lane. (A switch draws its own tree DOWN into the fan — see SequenceSwitchBranches.) */}
            {steps.map((step, i) => {
              if (step.type !== 'spawn') return null;
              const y0 = nodeY(i);
              return (
                <line
                  key={`branch-line-${i}`}
                  x1={nodeRightXFor(i)}
                  y1={y0}
                  x2={branchXFor(i)}
                  y2={y0}
                  className="stroke-lynch-300 dark:stroke-slate-600"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>

          {/* The owner tops the spine of the BUILDER. It's a property of the whole sequence, not a stage of
            anyone's journey, so the locked per-contact view doesn't spend a canvas row on it — the card
            header carries "runs as" there, next to the sequence's own name. */}
          {!readOnly && owner != null && (
            <div
              className="absolute z-30"
              // Anchored by the circle's LEFT edge (not the row's centre) so the avatar lands on the spine
              // while the name flows to its right — see OWNER_AVATAR_PX.
              style={{ left: CX - OWNER_AVATAR_PX / 2, top: TOP_ROW_Y, transform: 'translate(0, -50%)' }}>
              <SequenceOwnerNode
                teamId={teamId}
                ownerUserId={owner.userId}
                ownerName={owner.name}
                ownerProfilePicture={owner.profilePicture}
                onChangeOwner={onChangeOwner}
              />
            </div>
          )}

          {/* The locked per-contact view: the four automatic stages as ONE node. For one person the
            front-half is a single checkpoint — already done, or exactly where they're stuck — so it's
            drawn as one, with the stages behind a click and the live stage on the pill. */}
          {readOnly && (
            <Fragment>
              <div
                data-no-pan
                data-prestep
                // While they're in prep, the pill (with its live stage) IS "now" — keep it whole on open.
                data-canvas-focus-extra={inPrep ? true : undefined}
                className={cn(
                  'absolute',
                  expandedPreStep === SEQUENCE_PREP_STEP.id ? 'z-40' : 'z-10',
                  // Ringed exactly like a current STEP — same halo, same tone — so "where this contact is"
                  // reads identically whether they're still in prep or out in the sequence. Amber pill in a
                  // blue ring would teach two colour languages for one idea.
                  inPrep && 'rounded-full',
                  inPrep && prepVisual?.ring,
                )}
                style={{ left: CX, top: TOP_ROW_Y, transform: 'translate(-50%, -50%)' }}>
                <SequencePrepNode
                  done={!inPrep}
                  statusLabel={currentNodeId == null ? undefined : nodeStatusLabel(currentNodeId)}
                  tone={currentNodeId == null ? null : faceTone(currentNodeId)}
                  expanded={expandedPreStep === SEQUENCE_PREP_STEP.id}
                  onToggle={() =>
                    setExpandedPreStep((cur) => (cur === SEQUENCE_PREP_STEP.id ? null : SEQUENCE_PREP_STEP.id))
                  }
                />
              </div>
              {expandedPreStep === SEQUENCE_PREP_STEP.id && (
                <div
                  data-no-pan
                  data-prestep
                  className="absolute z-40"
                  style={{ left: CX + 96, top: TOP_ROW_Y, transform: 'translate(0, -20px)' }}>
                  <SequencePreStepDetail preStep={SEQUENCE_PREP_STEP} />
                </div>
              )}
            </Fragment>
          )}

          {/* Sits ON the dashed spine between the owner and the first pre-step, so it carries a background
            to occlude the line behind it (same trick as the "if not accepted" edge label). */}
          {!readOnly && (
            <div
              className="absolute whitespace-nowrap bg-slate-50 px-2 text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:bg-neutral-900 dark:text-gray-500"
              style={{ left: CX, top: PRE_TOP - STRIDE / 2, transform: 'translate(-50%, -50%)' }}>
              Automatic · research &amp; qualify
            </div>
          )}

          {!readOnly &&
            SEQUENCE_PRE_STEPS.map((preStep, p) => {
            const isOpen = expandedPreStep === preStep.id;
            return (
              <Fragment key={preStep.id}>
                <div
                  data-no-pan
                  data-prestep
                  className={cn('absolute', isOpen ? 'z-40' : 'z-10')}
                  style={{ left: CX, top: preCenterY(p), transform: 'translate(-50%, -50%)' }}>
                  <SequencePreStepNode
                    preStep={preStep}
                    expanded={isOpen}
                    onToggle={() => setExpandedPreStep((cur) => (cur === preStep.id ? null : preStep.id))}
                  />
                </div>
                {isOpen && (
                  <div
                    data-no-pan
                    data-prestep
                    // Top-anchored (opens downward) so a tall panel never runs off the top of the
                    // overflow-hidden canvas; -20px roughly aligns its header with the node.
                    className="absolute z-40"
                    style={{ left: CX + 96, top: preCenterY(p), transform: 'translate(0, -20px)' }}>
                    <SequencePreStepDetail preStep={preStep} />
                  </div>
                )}
              </Fragment>
            );
          })}

          {!readOnly &&
            Array.from({ length: n + 1 }).map((_, k) => {
            // A Switch ends the main line — its branches are terminal (no rejoin), so nothing can run after
            // it on the spine. Suppress the insert that would sit below a switch instead of offering an
            // "add a step" that only gets flagged invalid (see sequenceStepIssue).
            if (k > 0 && steps[k - 1]?.type === 'switch') return null;
            return (
              // While a picker is open, other "+" tiles stay visible but inert (no layout jump). The "+"
              // stays centered on the spine; its menu is portaled + floated (SequenceGraphInsert).
              <div
                key={`slot-${k}`}
                data-no-pan
                className={cn('absolute', pickerAt === k ? 'z-50' : 'z-20')}
                style={{ left: CX, top: slotY(k), transform: 'translate(-50%, -50%)' }}>
                <SequenceGraphInsert
                  active={pickerAt === k}
                  inert={anyPickerOpen && pickerAt !== k}
                  hovered={k === 0 && n === 0 && firstStepHintHovered}
                  onOpen={() => setPickerAt(k)}
                  onPick={(type) => insertAt(k, type)}
                  onPickContainer={(type) => insertContainerAt(k, type)}
                  onCancel={() => setPickerAt(null)}
                  disabledReason={(type) => insertDisabledReason(steps, k, type)}
                />
              </div>
            );
            })}

          {steps.map((step, i) => {
            // Locked view: mark this contact's progress — dim a spine step already sent (also non-clickable
            // in openEditor), and ring the one they're currently on. Nested-arm status is marked on its own node.
            const spineVisual = visualAt(sequenceOverrideKey([], i));
            return (
              <div
                key={`node-${i}`}
                data-no-pan
                data-node-index={i}
                data-line="spine"
                data-idx={i}
                // The painted per-contact state, exposed for tests (the tone is invisible to typecheck +
                // lint — only a rendered assertion can catch a node lying about where the contact is).
                data-step-state={spineVisual?.tone}
                onPointerDown={(e) => startDrag(e, i)}
                className={cn(
                  'absolute select-none',
                  dragSpineIndex === i
                    ? 'z-40 pointer-events-none opacity-30'
                    : groupPickerOpen === i
                      ? 'z-50'
                      : 'z-10',
                  spineVisual != null && spineVisual.ring !== '' && 'rounded-xl',
                  spineVisual?.ring,
                )}
                style={{ left: CX, top: nodeY(i), transform: 'translate(-50%, -50%)' }}>
                {/* A parallel group IS its button group — rendered inline on the spine (no separate icon /
                caption / side box). Dragging the box background reorders the whole group; dragging a
                chip pulls it out. */}
                {step.type === 'group' && (
                  <SequenceParallelArms
                    inline
                    step={step}
                    lineAddr={{ path: [{ at: i, into: 'group' }] }}
                    onStepDrag={readOnly ? undefined : startStepDrag}
                    draggingAddr={dragArmed ? dragSource : null}
                    onChange={(next) => updateStepAt(i, () => next)}
                    onRemove={readOnly ? undefined : () => remove(i)}
                    onEditArm={(arm) => openEditor({ line: { path: [{ at: i, into: 'group' }] }, index: arm })}
                    onPickerOpenChange={(open) => setGroupPickerOpen(open ? i : null)}
                    autoOpenPicker={autoOpenContainerAt === i}
                    readOnly={readOnly}
                  />
                )}
                {/* A switch IS its attribute pill on the spine (Age / ~AUM toggle); its route branches fan
                out below (rendered separately). Dragging the pill background reorders the node. */}
                {step.type === 'switch' && (
                  <SequenceSwitchNode
                    step={step}
                    invalid={sequenceStepIssue(steps, i)}
                    onChangeAttribute={(attribute) =>
                      updateStepAt(i, (s) => (s.switch == null ? s : { ...s, switch: { ...s.switch, attribute } }))
                    }
                    onRemove={() => remove(i)}
                    readOnly={readOnly}
                  />
                )}
                {step.type !== 'group' && step.type !== 'switch' && (
                  <SequenceGraphNode
                    step={step}
                    dragging={false}
                    invalid={sequenceStepIssue(steps, i)}
                    onRemove={() => remove(i)}
                    onChangeWait={(days, unit) => setWait(i, days, unit)}
                    onChangeDeadlineDays={(days) => setDeadlineDays(i, days)}
                    readOnly={readOnly}
                    visual={spineVisual}
                  />
                )}
              </div>
            );
          })}

          {/* The journey's end: a read-only terminal node closing the spine, with the pile of people who
            have completed the sequence beside it. */}
          {showFinished && (
            <>
              <div className="absolute z-10" style={{ left: CX, top: finishedY, transform: 'translate(-50%, -50%)' }}>
                {/* After a switch the main line already ended on the pill and the branch fan owns every row
                  between — so the terminal sits below the fan with no connector, and says so. */}
                <SequenceFinishedNode detached={endsInSwitch} />
              </div>
              {finishedFaces.total > 0 && (
                <div
                  className="absolute z-20"
                  style={{ left: CX - 66, top: finishedY, transform: 'translate(-100%, -50%)' }}>
                  <SequenceNodeFaces
                    sample={finishedFaces.sample}
                    total={finishedFaces.total}
                    onOpenContact={onOpenContactFace}
                    onRestart={onRestartFace}
                    onRemove={onRemoveFace}
                    onCompleteTask={onCompleteTaskFace}
                  />
                </div>
              )}
            </>
          )}

          {/* Content preview — a step's authored subject/body shown to the RIGHT of its node (the editor
            opens over this same column when you click it). Only content steps with text get a card. */}
          {steps.map((step, i) => {
            // Locked view: `step` is already THIS contact's content (see `steps`).
            if (!stepHasContentPreview(step)) return null;
            // Locked view: the preview is a click-to-edit target only when the step is actually editable —
            // a content send step that hasn't been sent yet. Otherwise it renders as static text (no dead
            // "Edit step content" click). The full editor always allows editing.
            const previewState = statusAt(sequenceOverrideKey([], i))?.state;
            const previewEditable =
              !readOnly ||
              (isStepContentEditableNow(statusAt(sequenceOverrideKey([], i))) && stepTakesContactOverride(step));
            return (
              <div
                key={`preview-${i}`}
                data-no-pan
                className={cn(
                  'absolute z-10',
                  dragSpineIndex === i && 'pointer-events-none opacity-30',
                  readOnly &&
                    (previewState === 'done' || previewState === 'skipped' || previewState === 'not_taken') &&
                    'opacity-60',
                )}
                style={{ left: previewLeftFor(i), top: nodeY(i), transform: 'translate(0, -50%)' }}>
                <SequenceStepPreview
                  step={step}
                  maxWidthPx={previewMaxWidthFor(i)}
                  onEdit={previewEditable ? () => openEditor({ line: SPINE, index: i }) : undefined}
                />
              </div>
            );
          })}

          {/* Locked view: when each step that ran went out, in the gutter beside it — the per-step slice of the
            contact's history, read straight off the graph. The step they're on carries the NOW marker
            there instead. */}
          {readOnly &&
            steps.map((step, i) => {
              const status = statusAt(sequenceOverrideKey([], i));
              if (status?.state !== 'done' || status.doneAt == null) return null;
              return (
                <div
                  key={`sent-${i}`}
                  data-sent-at={sequenceOverrideKey([], i)}
                  className="pointer-events-none absolute z-10 whitespace-nowrap text-[10px] font-medium text-lynch-400 dark:text-lynch-500"
                  style={{
                    left: Math.max(0, CX - spineNodeHalfWidth(step) - 10),
                    top: nodeY(i),
                    transform: 'translate(-100%, -50%)',
                  }}>
                  {format(status.doneAt, 'MMM d')}
                </div>
              );
            })}

          {/* Locked view: where this contact is right now, beside the step they're on — the per-contact
            answer to the builder's people-piles below (which never render here). No avatar: the page is
            already about this one person, so a face here would only repeat the header. The "Mark done"
            control rides in the same gutter row so the two can't overlap, and it appears only when the
            contact is parked on that step's call/reminder to-do. */}
          {readOnly &&
            steps.map((step, i) => {
              const visual = visualAt(sequenceOverrideKey([], i));
              if (visual == null || !visual.isHere) return null;
              const manualWaitNode = manualTaskWaitNodeForStepType(step.type);
              // Only where the arm is still going: completing a to-do on an ENDED (errored) arm advances
              // nothing, so the button would be a no-op wearing a success toast.
              const onMarkDone =
                visual.isLive && manualWaitNode != null && onCompleteManualStep != null
                  ? () => onCompleteManualStep(manualWaitNode, sequenceOverrideKey([], i))
                  : undefined;
              return (
                <div
                  key={`here-${i}`}
                  data-no-pan
                  data-canvas-focus-extra
                  className="absolute z-20"
                  // Hugs THIS node's left edge (a wait/await/group pill is far wider than an icon tile), so
                  // the marker never lands on top of the node it's marking. Clamped at the canvas edge: only
                  // the RIGHT side of the content box is reserved, so a very wide node (a many-armed group)
                  // would otherwise push the marker into negative x, where the overflow-hidden viewport
                  // clips it and "fit to screen" (which reads scrollWidth) can't bring it back.
                  style={{
                    left: Math.max(0, CX - spineNodeHalfWidth(step) - 10),
                    top: nodeY(i),
                    transform: 'translate(-100%, -50%)',
                  }}>
                  <SequenceCurrentStepMarker tone={visual.tone} stageLabel={visual.stageLabel} onMarkDone={onMarkDone} />
                </div>
              );
            })}

          {/* Faces — the people currently sitting on each node — in a gutter to the left of the spine.
            (Builder only for the stages: the locked view folds them into one node and has no pile.) */}
          {!readOnly &&
            SEQUENCE_PRE_STEPS.map((preStep, p) => {
            const f = facesFor(PRE_STEP_NODE_IDS[preStep.id] ?? []);
            if (f.total === 0) return null;
            return (
              <div
                key={`faces-pre-${preStep.id}`}
                className="absolute z-20"
                style={{ left: CX - 66, top: preCenterY(p), transform: 'translate(-100%, -50%)' }}>
                <SequenceNodeFaces
                  sample={f.sample}
                  total={f.total}
                  onOpenContact={onOpenContactFace}
                  onRestart={onRestartFace}
                  onRemove={onRemoveFace}
                  onCompleteTask={onCompleteTaskFace}
                />
              </div>
            );
          })}
          {steps.map((step, i) => {
            const f = facesForStep([], i);
            if (f.total === 0) return null;
            return (
              <div
                key={`faces-node-${i}`}
                // The step address this pile belongs to — the identity the placement is about, so a test
                // can assert WHICH step drew it (same convention as `data-arm-step` below).
                data-faces-at={sequenceOverrideKey([], i)}
                className="absolute z-20"
                style={{ left: CX - 66, top: nodeY(i), transform: 'translate(-100%, -50%)' }}>
                <SequenceNodeFaces
                  sample={f.sample}
                  total={f.total}
                  onOpenContact={onOpenContactFace}
                  onRestart={onRestartFace}
                  onRemove={onRemoveFace}
                  // Bound to THIS step's address, so "Mark done" completes the to-do the rep clicked
                  // rather than every arm of that contact parked on the same kind of lane.
                  onCompleteTask={(face) => onCompleteTaskFace(face, sequenceOverrideKey([], i))}
                />
              </div>
            );
          })}

          {/* Not-accepted arms off await_connect steps (Withdraw → …). */}
          {steps.map((step, i) => {
            if (step.type !== 'await_connect') return null;
            const arm = step.branches?.notAccepted ?? null;
            const y0 = nodeY(i);
            const ax = armXFor(i);

            if (arm == null) {
              // Locked view: no "if not accepted" branch authored — nothing to show (can't add one here).
              if (readOnly) return null;
              return (
                <div
                  key={`arm-stub-${i}`}
                  data-no-pan
                  className="absolute z-10"
                  // Sits just past the await pill's right edge (whatever its width) — clear of the node.
                  style={{ left: lanes.stubXFor(i), top: y0, transform: 'translate(0, -50%)' }}>
                  <Button size="2xs" color="red" tone="soft" icon={<General.Plus />} onClick={() => addArm(i)}>
                    if not accepted
                  </Button>
                </div>
              );
            }

            return (
              <Fragment key={`arm-${i}`}>
                {/* Edge label centered ON the connector, in the clear span between the await pill's right
                  edge and the Withdraw node (its bg occludes the rose line under it). The arm is inset
                  (NOT_ACCEPTED_ARM_INSET) so this wide label fits between the pills without overlapping. */}
                <div
                  data-no-pan
                  className="absolute z-10 flex items-center gap-1 rounded-full bg-slate-50 px-2 dark:bg-neutral-900"
                  style={{ left: (nodeRightXFor(i) + ax - WITHDRAW_HALF_W) / 2, top: y0, transform: 'translate(-50%, -50%)' }}>
                  <span
                    className={cn(
                      'whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide',
                      armVisualAt(i, 0)?.tone === 'notTaken' ? 'text-lynch-400 dark:text-slate-500' : 'text-rose-400',
                    )}>
                    if not accepted
                  </span>
                  {!readOnly && (
                    <Button size="2xs" text icon={<General.XMark />} tooltip="Remove branch" onClick={() => removeArm(i)} />
                  )}
                </div>

                {arm.map((armStep, p) => {
                  const armY = y0 + p * STRIDE;
                  const armFaces = facesForStep([i, NOT_ACCEPTED_SEG], p);
                  return (
                    <Fragment key={`arm-${i}-node-${p}`}>
                      <div
                        data-no-pan
                        data-arm-step={`${i}.${p}`}
                        data-step-state={armVisualAt(i, p)?.tone}
                        className={cn('absolute z-10', armVisualAt(i, p)?.ring)}
                        style={{ left: ax, top: armY, transform: 'translate(-50%, -50%)' }}>
                        {armStep.type === 'withdraw' && (
                          <span
                            className={cn(
                              'flex h-9 cursor-default items-center gap-1.5 rounded-full border border-rose-200 bg-white px-3 text-xs font-medium text-rose-500 shadow-sm dark:border-rose-500/40 dark:bg-slate-900 dark:text-rose-300 [&>svg]:size-4',
                              armVisualAt(i, p)?.chipWide,
                            )}>
                            {STEP_META.withdraw.icon}
                            Withdraw invite
                          </span>
                        )}
                        {armStep.type !== 'withdraw' && (
                          <SequenceGraphNode
                            step={armStep}
                            dragging={false}
                            invalid={sequenceStepIssue(arm, p)}
                            onRemove={readOnly ? undefined : () => removeArmStep(i, p)}
                            onChangeWait={readOnly ? undefined : (days, unit) => setArmWait(i, p, days, unit)}
                            onChangeDeadlineDays={readOnly ? undefined : (days) => setArmDeadlineDays(i, p, days)}
                            readOnly={readOnly}
                            visual={armVisualAt(i, p)}
                          />
                        )}
                      </div>

                      {armFaces.total > 0 && (
                        <div
                          data-faces-at={sequenceOverrideKey([i, NOT_ACCEPTED_SEG], p)}
                          className="absolute z-20"
                          style={{ left: ax - 66, top: armY, transform: 'translate(-100%, -50%)' }}>
                          <SequenceNodeFaces
                            sample={armFaces.sample}
                            total={armFaces.total}
                            onOpenContact={onOpenContactFace}
                            onRestart={onRestartFace}
                            onRemove={onRemoveFace}
                            onCompleteTask={(face) =>
                              onCompleteTaskFace(face, sequenceOverrideKey([i, NOT_ACCEPTED_SEG], p))
                            }
                          />
                        </div>
                      )}

                      {!readOnly && (
                        <div
                          data-no-pan
                          className={cn('absolute', isArmPicker(i, p + 1) ? 'z-50' : 'z-20')}
                          style={{ left: ax, top: armY + STRIDE / 2, transform: 'translate(-50%, -50%)' }}>
                          <SequenceGraphInsert
                            active={isArmPicker(i, p + 1)}
                            inert={anyPickerOpen && !isArmPicker(i, p + 1)}
                            onOpen={() => setArmPickerAt({ step: i, arm: p + 1 })}
                            onPick={(type) => insertArmStep(i, p + 1, type)}
                            onCancel={() => setArmPickerAt(null)}
                            disabledReason={(type) => insertDisabledReason(arm, p + 1, type)}
                          />
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </Fragment>
            );
          })}

          {/* A spawn is a side branch ("runs alongside"), so its box is drawn to the RIGHT of the node.
            (A group renders inline on the spine — see the node map above.) */}
          {steps.map((step, i) => {
            if (step.type !== 'spawn') return null;
            return (
              <div
                key={`parallel-${i}`}
                data-no-pan
                className={cn('absolute z-10', dragSpineIndex === i && 'pointer-events-none opacity-30')}
                // TOP-aligned + centered on its lane x: the branch's line starts exactly at its node's row
                // (where the horizontal connector meets it) and grows DOWN — never up into an earlier row —
                // so an earlier branch's connector crosses this lane only where it's still empty.
                style={{ left: branchXFor(i), top: nodeY(i), transform: 'translate(-50%, 0)' }}>
                <SequenceParallelArms
                  step={step}
                  lineAddr={{ path: [{ at: i, into: 'spawn' }] }}
                  onStepDrag={readOnly ? undefined : startStepDrag}
                  draggingAddr={dragArmed ? dragSource : null}
                  onChange={(next) => updateStepAt(i, () => next)}
                  onRemove={readOnly ? undefined : () => remove(i)}
                  onInsertContainer={readOnly ? undefined : (slot, type) => insertSpawnArmContainer(i, slot, type)}
                  onEditArm={(arm) => openEditor({ line: { path: [{ at: i, into: 'spawn' }] }, index: arm })}
                  autoOpenPicker={autoOpenContainerAt === i}
                  readOnly={readOnly}
                />
              </div>
            );
          })}

          {/* A switch fans its route branches DOWN below the node (each branch its own terminal path).
            The fan is anchored so column 0 sits ON the spine (left = CX − half a column) and starts
            just below the pill; its own SVG draws the tree from the node down into every column. */}
          {steps.map((step, i) => {
            if (step.type !== 'switch') return null;
            return (
              <div
                key={`switch-${i}`}
                // NOT data-no-pan: the fan is huge (branches + flaired arms), and blanketing it would kill
                // panning across all that empty space. Its interactive bits (range chips, wait inputs, "+"
                // pickers, remove ✕) are buttons/inputs that suppress pan on their own; empty gutters pan.
                className={cn(
                  'absolute',
                  dragSpineIndex === i && 'pointer-events-none opacity-30',
                  switchPickerOpen === i ? 'z-50' : 'z-10',
                )}
                style={{ left: switchFanLeft(CX), top: nodeY(i) + SWITCH_BELOW, transform: 'translate(0, 0)' }}>
                <SequenceSwitchBranches
                  step={step}
                  switchIndex={i}
                  onStepDrag={readOnly ? undefined : startStepDrag}
                  draggingAddr={dragArmed ? dragSource : null}
                  onChange={(next) => updateStepAt(i, () => next)}
                  onRemove={readOnly ? undefined : () => remove(i)}
                  onEditBranchStep={(branch, arm) => openEditor({ line: { path: [{ at: i, into: 'branch', index: branch }] }, index: arm })}
                  onEditBranchContainerStep={(branch, slot, arm) => openEditorForContainerArm(i, branch, slot, arm)}
                  autoOpenPicker={autoOpenContainerAt === i}
                  onPickerOpenChange={(open) => setSwitchPickerOpen(open ? i : null)}
                  readOnly={readOnly}
                />
              </div>
            );
          })}

          {/* Step content editor — a PORTALED popover anchored (Floating UI) to the clicked node, so it
            always opens right next to that exact node, on-screen, at natural size, no matter how deep it
            is nested or how zoomed the canvas is. Builder: saves as you type, and ⌘/Ctrl+Enter, Escape or a
            click outside save & close. In readOnly mode it edits THIS contact's override (body/subject only),
            not the template, saved on close. */}
          {editing != null && editorStep != null && (
            <FloatingPortal>
              <div ref={editorFloat.refs.setFloating} data-no-pan data-step-editor className="z-50" style={editorFloat.floatingStyles}>
                <SequenceStepEditor
                  key={
                    `${serializeLine(editing.line)}#${editing.index}`
                  }
                  step={editorStep}
                  onChange={updateEditingStepLocal}
                  onClose={closeEditor}
                  variables={variables}
                  documents={documents}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  reminderCreating={reminderCreating}
                  contentOnly={readOnly}
                  previousEmailSubject={editingPrevEmailSubject}
                  issue={editorIssue}
                />
              </div>
            </FloatingPortal>
          )}

          {/* Keyed by step, so moving to another step's save slides one tab away and the next one out. */}
          <AnimatePresence>
            {stepSave != null && stepSavePos != null && (
              <SequenceStepSaveTab
                key={`${serializeLine(stepSave.addr.line)}#${stepSave.addr.index}`}
                status={stepSave.status}
                x={stepSavePos.x}
                y={stepSavePos.y}
              />
            )}
          </AnimatePresence>

          {n === 0 && !readOnly && (
            <div className="absolute" style={{ left: CX, top: nodeY(0), transform: 'translate(-50%, -50%)' }}>
              {/* The hint is a second trigger for the leading "+": clicking it opens the picker, hovering it
                animates the "+" (via firstStepHintHovered → SequenceGraphInsert `hovered`). */}
              <Button
                text
                data-no-pan
                onClick={() => setPickerAt(0)}
                onMouseEnter={() => setFirstStepHintHovered(true)}
                onMouseLeave={() => setFirstStepHintHovered(false)}
                className="whitespace-nowrap text-sm font-normal text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400">
                Click + to add your first step
              </Button>
            </div>
          )}
        </div>
      </SequencePanZoom>

      <div className="pointer-events-none absolute right-3 top-3 z-10 flex h-5 items-center">
        {/* A step's text save shows on that step (its tab); this corner speaks for the rest — structure edits. */}
        {updateMutation.isPending && stepSave?.status !== 'saving' && (
          <span className="text-xs text-gray-400">Saving…</span>
        )}
        {updateMutation.isError && stepSave?.status !== 'error' && (
          <span className="text-xs text-red-500">Couldn&apos;t save — try again</span>
        )}
      </div>

      {/* The sequence as one line of text (`sequenceText`) — a live read-out of the SHAPE you've built, for
          reading and quoting. Not a hand-off format: the line carries no message or email content (that
          rides in the sidecar `config`), so it can't rebuild a sequence on its own. Builder only — in the
          locked per-contact view it's noise for a rep. */}
      {!readOnly && <SequenceTextStrip steps={steps} />}

      {/* Catch-all pile for people parked in a state no authored step places on the canvas (shared park
          lanes, needs-attention, an unmodelled node). Keeps the editor honest — every enrolled contact is
          reachable here even when their state isn't tied to one step. */}
      {offGraph.total > 0 && (
        <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50/95 px-2.5 py-1.5 shadow-sm dark:border-amber-700 dark:bg-amber-950/80">
          <Tooltip
            tooltipMaxWidth={320}
            tooltip={
              <SequenceOtherStatesTooltip
                intro="People parked in a state no authored step places on the canvas — a shared send lane or a post-outreach outcome. They're still enrolled; open one to see exactly where they are."
                states={offGraph.states}
              />
            }>
            {/* Focusable: this tooltip holds the only per-state counts anywhere in the UI, so a keyboard
                (or screen-reader) user has to be able to reach it — a bare <span> never takes focus. */}
            <span tabIndex={0} className="text-xs font-medium text-amber-700 dark:text-amber-300">
              Other states
            </span>
          </Tooltip>
          <SequenceNodeFaces
            sample={offGraph.sample}
            total={offGraph.total}
            onOpenContact={onOpenContactFace}
            onRestart={onRestartFace}
            onRemove={onRemoveFace}
            onCompleteTask={onCompleteTaskFace}
          />
        </div>
      )}

      {/* Drag ghost — a screen-space copy of the dragged node that tracks the cursor. Portaled to the
          body so no pan/zoom transform (or overflow clip) affects it; pointer-events-none so it never
          blocks the drop hit-test underneath. */}
      {draggedStep != null &&
        dragArmed &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={ghostRef}
            className="pointer-events-none fixed left-0 top-0 z-[60] opacity-90"
            style={{
              transform: `translate(${pointerRef.current.x}px, ${pointerRef.current.y}px) translate(-50%, -50%)`,
            }}>
            <SequenceNodePreview step={draggedStep} />
          </div>,
          document.body,
        )}

      {/* Reorder insertion bar — a screen-space line placed imperatively at the target gap during a drag (in
          ANY surface). Hidden by default; the move handler positions + shows it. Portaled so no pan/zoom
          transform affects it. */}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropBarRef}
            className="pointer-events-none fixed z-[55] rounded-full bg-blue-500"
            style={{ display: 'none' }}
          />,
          document.body,
        )}
    </div>
  );
}
